import { centerCrop, makeAspectCrop, type Crop, type PixelCrop } from 'react-image-crop'

const MAX_AVATAR_BYTES = 5 * 1024 * 1024
const ACCEPTED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const OUTPUT_SIZE = 512
const OUTPUT_TYPE = 'image/webp'
const OUTPUT_QUALITY = 0.82
const AVATAR_ASPECT_RATIO = 1

export interface AvatarSourceDimensions {
  width: number
  height: number
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const image = new Image()

    image.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(image)
    }

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('The selected image could not be read.'))
    }

    image.src = objectUrl
  })
}

export function validateImageUploadFile(file: File) {
  if (!ACCEPTED_AVATAR_TYPES.includes(file.type)) {
    throw new Error('Use a JPG, PNG, or WebP image.')
  }

  if (file.size > MAX_AVATAR_BYTES) {
    throw new Error('The image must be smaller than 5 MB.')
  }
}

export function validateAvatarFile(file: File) {
  validateImageUploadFile(file)
}

export async function readAvatarSourceDimensions(file: File): Promise<AvatarSourceDimensions> {
  validateAvatarFile(file)

  const image = await loadImage(file)

  return {
    width: image.naturalWidth,
    height: image.naturalHeight,
  }
}

export function createDefaultAvatarCrop(source: AvatarSourceDimensions): Crop {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 100,
      },
      AVATAR_ASPECT_RATIO,
      source.width,
      source.height,
    ),
    source.width,
    source.height,
  )
}

export async function compressAvatarFile(file: File, crop?: PixelCrop | null) {
  validateAvatarFile(file)

  const image = await loadImage(file)
  const safeCrop = normalizePixelCrop(
    crop ?? getFallbackPixelCrop(image.naturalWidth, image.naturalHeight),
    image.naturalWidth,
    image.naturalHeight,
  )
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  if (context === null) {
    throw new Error('Canvas compression is not available in this browser.')
  }

  canvas.width = OUTPUT_SIZE
  canvas.height = OUTPUT_SIZE

  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(
    image,
    safeCrop.x,
    safeCrop.y,
    safeCrop.width,
    safeCrop.height,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE,
  )

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, OUTPUT_TYPE, OUTPUT_QUALITY)
  })

  if (blob === null) {
    throw new Error('The image could not be compressed.')
  }

  const fileName = file.name.replace(/\.[^.]+$/, '') || 'avatar'

  return new File([blob], `${fileName}.webp`, {
    type: OUTPUT_TYPE,
    lastModified: Date.now(),
  })
}

function getFallbackPixelCrop(width: number, height: number): PixelCrop {
  const cropSize = Math.min(width, height)
  const x = Math.round((width - cropSize) / 2)
  const y = Math.round((height - cropSize) / 2)

  return {
    unit: 'px',
    x,
    y,
    width: cropSize,
    height: cropSize,
  }
}

function normalizePixelCrop(crop: PixelCrop, sourceWidth: number, sourceHeight: number): PixelCrop {
  const width = clamp(Math.round(crop.width), 1, sourceWidth)
  const height = clamp(Math.round(crop.height), 1, sourceHeight)
  const x = clamp(Math.round(crop.x), 0, Math.max(0, sourceWidth - width))
  const y = clamp(Math.round(crop.y), 0, Math.max(0, sourceHeight - height))

  return {
    unit: 'px',
    x,
    y,
    width,
    height,
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

